import { z } from "zod";
import { LocalDateSchema, PlanningOpaqueIdSchema, PlanningRequestIdSchema } from "./planning";

const CountSchema = z.number().int().min(0);

export const DashboardRateSchema = z
  .object({
    numerator: CountSchema,
    denominator: CountSchema,
    percent: z.number().min(0).max(100).nullable(),
    unavailable_reason: z.string().min(1).max(240).nullable(),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.numerator > value.denominator) {
      context.addIssue({
        code: "custom",
        message: "rate numerator cannot exceed denominator",
        path: ["numerator"],
      });
    }
    if (value.denominator === 0) {
      if (value.percent !== null || value.unavailable_reason === null) {
        context.addIssue({
          code: "custom",
          message: "an empty rate requires an unavailable reason and no percent",
          path: ["percent"],
        });
      }
      return;
    }
    if (value.percent === null || value.unavailable_reason !== null) {
      context.addIssue({
        code: "custom",
        message: "an available rate requires a percent and no unavailable reason",
        path: ["percent"],
      });
    }
  });

export const DashboardTaskBreakdownSchema = z
  .object({
    total: CountSchema,
    planned: CountSchema,
    ready: CountSchema,
    in_progress: CountSchema,
    blocked: CountSchema,
    done: CountSchema,
    verified: CountSchema,
    needs_changes: CountSchema,
    deferred: CountSchema,
    skipped: CountSchema,
    cancelled: CountSchema,
    verification_waiting: CountSchema,
  })
  .strict();

export const DashboardDailyLoadSchema = z
  .object({
    local_date: LocalDateSchema,
    planned_count: CountSchema,
    completed_count: CountSchema,
    estimated_minutes: CountSchema,
    missing_estimate_count: CountSchema,
  })
  .strict();

export const DashboardRoutineTrendSchema = z
  .object({
    routine_id: PlanningOpaqueIdSchema,
    name: z.string().min(1).max(160),
    child_copy: z.string().min(1).max(160),
    planned_count: CountSchema,
    completed_count: CountSchema,
    deferred_count: CountSchema,
    skipped_count: CountSchema,
    completion_rate: DashboardRateSchema,
  })
  .strict();

export const DashboardProjectProgressSchema = z
  .object({
    project_id: PlanningOpaqueIdSchema,
    name: z.string().min(1).max(160),
    status: z.enum(["planned", "active", "paused", "completed", "cancelled"]),
    task_total: CountSchema,
    task_completed: CountSchema,
    milestone_total: CountSchema,
    milestone_completed: CountSchema,
  })
  .strict();

export const DashboardTermProgressSchema = z
  .object({
    term_id: PlanningOpaqueIdSchema,
    name: z.string().min(1).max(120),
    task_total: CountSchema,
    task_completed: CountSchema,
  })
  .strict();

export const DashboardHighlightSchema = z
  .object({
    evidence_id: PlanningOpaqueIdSchema,
    task_id: PlanningOpaqueIdSchema,
    assignment_id: PlanningOpaqueIdSchema,
    child_id: PlanningOpaqueIdSchema,
    evidence_type: z.enum(["photo", "note"]),
    task_title: z.string().min(1).max(240),
    child_copy: z.string().min(1).max(160),
    note_text: z.string().min(1).max(2000).nullable(),
    created_at: z.number().int(),
  })
  .strict();

export const DashboardResponseSchema = z
  .object({
    range: z
      .object({
        from_local_date: LocalDateSchema,
        to_local_date: LocalDateSchema,
        timezone: z.string().min(1).max(64),
        child_id: PlanningOpaqueIdSchema.nullable(),
      })
      .strict(),
    task_breakdown: DashboardTaskBreakdownSchema,
    self_start_rate: DashboardRateSchema,
    no_start_reminder_rate: DashboardRateSchema,
    help: z
      .object({
        requested_count: CountSchema,
        resolved_count: CountSchema,
        open_count: CountSchema,
        average_resolution_minutes: z.number().min(0).nullable(),
      })
      .strict(),
    daily_load: z.array(DashboardDailyLoadSchema).max(366),
    routines: z.array(DashboardRoutineTrendSchema).max(100),
    projects: z.array(DashboardProjectProgressSchema).max(100),
    terms: z.array(DashboardTermProgressSchema).max(100),
    highlights: z.array(DashboardHighlightSchema).max(12),
    data_quality: z
      .object({
        assignments_without_estimate: CountSchema,
        adult_proxy_completion_count: CountSchema,
        agent_completion_count: CountSchema,
        latest_change_sequence: CountSchema,
        caveats: z.array(z.string().min(1).max(300)).max(20),
      })
      .strict(),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export type DashboardResponse = z.infer<typeof DashboardResponseSchema>;
